import type { GlowApiError } from '@innovamat/glow-api-client';
import {
  useEnrollmentBulkImportMutation,
  type ClassroomBody,
} from '@innovamat/glow-api-client';

type AddStudents = {
  classroomBody: ClassroomBody;
  emails: string[];
};

type EnrollmentBulkImportFailure = {
  classroomId: string;
  errorMessage: string;
  student: string;
  success: boolean;
};

type Props = {
  onSuccess: (classroomBody: ClassroomBody) => void;
  onError?: (error: GlowApiError) => void;
};

function useStudentsEnrollment({ onSuccess, onError }: Props): {
  handleAddStudents: ({ emails, classroomBody }: AddStudents) => void;
  isLoadingStudentsMutation: boolean;
  failures: EnrollmentBulkImportFailure[] | undefined;
} {
  const {
    data,
    mutate: updateClassroomEnrollment,
    isPending: isLoadingStudentsMutation,
  } = useEnrollmentBulkImportMutation<GlowApiError>();

  const handleAddStudents = ({ emails, classroomBody }: AddStudents): void => {
    if (!classroomBody || emails.length === 0) {
      return;
    }

    const enrollments = emails.map((email) => ({
      student: email,
      classroomId: classroomBody.id,
    }));

    updateClassroomEnrollment(
      {
        body: {
          enrollments,
        },
      },
      {
        onSuccess(response) {
          if (response.enrollmentBulkImport.failureCount === 0) {
            onSuccess(classroomBody);
          }
        },
        onError: onError,
      }
    );
  };

  return {
    failures: data?.enrollmentBulkImport.failures,
    handleAddStudents,
    isLoadingStudentsMutation,
  };
}

export { useStudentsEnrollment };
export type { EnrollmentBulkImportFailure };
